import React from 'react';

import { useIntl } from 'react-intl';

import AvatarGroup from '@atlaskit/avatar-group/avatar-group';

import Button from '@atlaskit/button/default/button';

import IconButton from '@atlaskit/button/icon/button';

import Heading from '@atlaskit/heading/heading';

import IconTile from '@atlaskit/icon/icon-tile';

import CrossIcon from '@atlaskit/icon/core/cross';

import { Inline } from '@atlaskit/primitives/compiled/inline';

import { Stack } from '@atlaskit/primitives/compiled/stack';

import { Text } from '@atlaskit/primitives/compiled/text';

import { useMessageContext } from '@atlassian/post-office-context/message-context';

import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { type EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context';

import { getIcon } from '../utils/icon-config';

import { toCamelCase } from '../utils/string';

import { CompactCard } from './compact-card';

import { messages } from './common-messages';

import { RecommendationContainer } from './recommendation-container';

import type { RecommendationButtonType } from './recommendation-button-type';

/** Call to action */
export type RecommendationCardCta = {
	isPrimary: boolean | null;
	label: string;
	url?: string | null;
	actionType?: RecommendationButtonType | null;
	prompt?: string | null;
};

export type RecommendationCardContext = {
	title: string;
	icon: string;
	body: string;
	ctas: RecommendationCardCta[];
	recommendationId: string;
	/**
	 * Optional list of requesting-user avatars ({@link src} image URL + display {@link name}).
	 * When present and non-empty, the card header renders a stacked avatar group in place of the
	 * {@link IconTile} icon (used by the "Review requests" use case). When empty/absent, the icon
	 * is rendered as before.
	 */
	avatars?: Array<{ src: string; name: string }>;
};

/** Visual appearance of the recommendation card. */
export type RecommendationCardAppearance = 'default' | 'compact';

export interface RecommendationCardProps extends RecommendationCardContext {
	onCtaClick?(cta: RecommendationCardCta): void;
	dismiss(messageInstanceId: string): void;
	// eligibleMessages is used only to send down to the analytics event
	eligibleMessages: (EligibleMessage & RecommendationCardContext)[];
	appearance?: RecommendationCardAppearance;
	/** Compact appearance only: hides the row's bottom divider so the last row sits flush. */
	isLast?: boolean;
}

export const RecommendationCard: React.FC<RecommendationCardProps> = ({
	title,
	icon,
	body,
	ctas,
	recommendationId,
	avatars,
	onCtaClick,
	dismiss,
	// eligibleMessages is used only to send down to the analytics event
	eligibleMessages,
	appearance = 'default',
	isLast = false,
}) => {
	const { formatMessage } = useIntl();
	const { messageInstanceViewed } = useMessageInstanceEvent();
	const { onCtaActioned, onClosed } = usePostOfficeUserInteractions();
	const { messageInstanceId } = useMessageContext();

	React.useEffect(() => {
		// Since we return null when messageInstanceId is not defined,
		// we shouldn't send a viewed event either
		if (!messageInstanceViewed || !messageInstanceId) return;

		void messageInstanceViewed({
			recommendationId,
			// Note: At time of writing this, additionalMetadataFromFrontend does not support array as a value type
			// TODO: revisit and ask PO if we can update type
			eligibleMessages: eligibleMessages.map((m) => m.recommendationId) as unknown as string,
		});
	}, [messageInstanceViewed, messageInstanceId, recommendationId]);

	if (!messageInstanceId) {
		return null;
	}

	const handleDismiss = () => {
		void onClosed({
			interactionIdentifier: 'dismissRecommendationCard',
			additionalMetadata: {
				recommendationId,
			},
		});

		dismiss(messageInstanceId);
	};

	const handleOnCtaClick = (cta: RecommendationCardCta) => {
		return () => {
			if (!onCtaClick) {
				return;
			}

			onCtaClick({
				isPrimary: cta.isPrimary,
				label: cta.label,
				url: cta.url,
				actionType: cta.actionType,
				prompt: cta.prompt,
			});

			void onCtaActioned({
				interactionIdentifier: toCamelCase(cta.label),
				destinationUrl: cta.url ?? '',
				additionalMetadata: {
					recommendationId,
				},
			});
		};
	};

	if (appearance === 'compact') {
		// Renders a single row; the placement wraps the mapped rows in the list container
		// and passes isLast so the final row's divider sits flush.
		return (
			<CompactCard
				title={title}
				icon={icon}
				body={body}
				ctas={ctas}
				avatars={avatars}
				onDismiss={handleDismiss}
				handleOnCtaClick={handleOnCtaClick}
				messageInstanceId={messageInstanceId}
				isLast={isLast}
			/>
		);
	}

	return (
		<RecommendationContainer testId="recommendation-card">
			<Stack space="space.200">
				<Stack space="space.100">
					<Inline space="space.100" spread="space-between" alignBlock="center">
						<Inline space="space.100" alignBlock="center">
							{avatars && avatars.length > 0 ? (
								<AvatarGroup
									appearance="stack"
									size="small"
									maxCount={3}
									testId="recommendation-card-avatars"
									data={avatars}
								/>
							) : (
								<IconTile
									icon={getIcon(icon)}
									appearance="purple"
									label=""
									size="medium"
									testId={icon}
								/>
							)}
							<Heading size="small">{title}</Heading>
						</Inline>
						<IconButton
							icon={CrossIcon}
							appearance="subtle"
							label={formatMessage(messages.crossIconLabel)}
							onClick={handleDismiss}
						/>
					</Inline>
					{/* TODO: AI-Native finalize show more functionality & designs */}
					<Text>{body}</Text>
				</Stack>
				<Inline space="space.100" shouldWrap={true} testId="recommendation-card-ctas">
					{[...ctas]
						.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))
						.map((cta) => (
							<Button
								appearance={cta.isPrimary ? 'primary' : 'default'}
								onClick={handleOnCtaClick(cta)}
								key={`${messageInstanceId}-${cta.label}`}
							>
								{cta.label}
							</Button>
						))}
				</Inline>
			</Stack>
		</RecommendationContainer>
	);
};
